/**
 * Prescription Template Builder (PRD.218).
 *
 * Create or edit a single prescription template along with its medicine
 * rows. The medicine row UI mirrors the encounter Prescription tab so a
 * template can be applied 1:1 into a patient prescription in PRD.219.
 *
 * Save flow:
 *  - new template: INSERT template, INSERT each item.
 *  - edit template: UPDATE template, DELETE existing items, INSERT current
 *    rows. The replace-all approach matches PRD.218 §11 "Replace all item
 *    rows for the template inside one transaction" and keeps the data path
 *    simple. Sequential `db.execute()` is intentional — `writeTransaction`
 *    bypasses PowerSync's CRUD upload (per project memory).
 *
 * Snapshot rule (PRD.218 §14): when a row is bound to an inventory item,
 * snapshot fields are populated from the picker selection. They are not
 * recomputed on later inventory edits, so template display stays stable.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavIcon, SidebarToggleButton, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  pickerCandidateRowToSelection,
  qInventoryPickerCandidates,
  qPrescriptionTemplateById,
  qPrescriptionTemplateItems,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
  type PrescriptionTemplateItemRow,
  type PrescriptionTemplateRow,
} from '@helios/data-access';

// ─── Field options (must match patient_prescriptions UI) ───

const INTAKE_TIMING_OPTIONS = [
  { value: '', label: 'Select timing…' },
  { value: 'before_food', label: 'Before Food' },
  { value: 'after_food', label: 'After Food' },
  { value: 'with_food', label: 'With Food' },
  { value: 'empty_stomach', label: 'Empty Stomach' },
  { value: 'anytime', label: 'Anytime' },
] as const;

const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
] as const;

// ─── Draft (mirror of patient_prescriptions fields) ───

interface ItemDraft {
  id: string;
  inventoryItemId: string | null;
  itemName: string;
  itemDetails: string;
  // Snapshots from inventory at authoring time
  itemNameSnapshot: string | null;
  skuSnapshot: string | null;
  genericNameSnapshot: string | null;
  brandNameSnapshot: string | null;
  strengthSnapshot: string | null;
  dosageFormSnapshot: string | null;
  routeSnapshot: string | null;
  unitSnapshot: string | null;
  // Prescribing fields (same shape as patient_prescriptions)
  intakeTiming: string;
  doseQuantity: string;
  scheduleMorning: boolean;
  scheduleAfternoon: boolean;
  scheduleNight: boolean;
  durationValue: string; // kept as string for input ergonomics
  durationUnit: string;
  instructions: string;
  sortOrder: number;
}

function emptyItemDraft(sortOrder: number): ItemDraft {
  return {
    id: crypto.randomUUID(),
    inventoryItemId: null,
    itemName: '',
    itemDetails: '',
    itemNameSnapshot: null,
    skuSnapshot: null,
    genericNameSnapshot: null,
    brandNameSnapshot: null,
    strengthSnapshot: null,
    dosageFormSnapshot: null,
    routeSnapshot: null,
    unitSnapshot: null,
    intakeTiming: '',
    doseQuantity: '',
    scheduleMorning: false,
    scheduleAfternoon: false,
    scheduleNight: false,
    durationValue: '',
    durationUnit: 'days',
    instructions: '',
    sortOrder,
  };
}

function rowToDraft(row: PrescriptionTemplateItemRow): ItemDraft {
  return {
    id: row.id,
    inventoryItemId: row.inventory_item_id,
    itemName: row.item_name,
    itemDetails: row.item_details ?? '',
    itemNameSnapshot: row.item_name_snapshot,
    skuSnapshot: row.sku_snapshot,
    genericNameSnapshot: row.generic_name_snapshot,
    brandNameSnapshot: row.brand_name_snapshot,
    strengthSnapshot: row.strength_snapshot,
    dosageFormSnapshot: row.dosage_form_snapshot,
    routeSnapshot: row.route_snapshot,
    unitSnapshot: row.unit_snapshot,
    intakeTiming: row.intake_timing ?? '',
    doseQuantity: row.dose_quantity ?? '',
    scheduleMorning: row.schedule_morning === 1,
    scheduleAfternoon: row.schedule_afternoon === 1,
    scheduleNight: row.schedule_night === 1,
    durationValue: row.duration_value != null ? String(row.duration_value) : '',
    durationUnit: row.duration_unit ?? 'days',
    instructions: row.instructions ?? '',
    sortOrder: row.sort_order,
  };
}

/** Build the item_details snapshot summary from inventory selection fields. */
function buildItemDetailsFromSelection(s: InventoryPickerSelection): string {
  return [s.brandName, s.strength, s.dosageForm].filter(Boolean).join(' · ');
}

// ─── Component ───

export function PrescriptionTemplateBuilder({
  templateId,
  orgId,
  userId,
  db,
  collapsed,
  onToggleSidebar,
  onClose,
}: {
  templateId: string | null;
  orgId: string;
  userId: string | null;
  db: ReturnType<typeof usePowerSyncDb>['db'];
  collapsed: boolean;
  onToggleSidebar?: () => void;
  onClose: () => void;
}) {
  const isNew = templateId == null;
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  // Existing template + items
  const { data: existing = [] } = usePowerSyncQuery<PrescriptionTemplateRow>(
    qPrescriptionTemplateById(templateId ?? ''),
  );
  const { data: existingItems = [] } = usePowerSyncQuery<PrescriptionTemplateItemRow>(
    qPrescriptionTemplateItems(templateId ?? ''),
  );
  const existingTemplate = isNew ? null : (existing[0] ?? null);

  // ─── Form state ───
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [defaultInstructions, setDefaultInstructions] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [hydrated, setHydrated] = useState(isNew);
  const [errors, setErrors] = useState<{
    name?: string;
    items?: string;
    rows?: Record<string, string>;
  }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) {
      if (items.length === 0) setItems([emptyItemDraft(0)]);
      return;
    }
    if (hydrated) return;
    if (!existingTemplate) return;
    setName(existingTemplate.name ?? '');
    setDescription(existingTemplate.description ?? '');
    setCategory(existingTemplate.category ?? '');
    setSpecialty(existingTemplate.specialty ?? '');
    setDefaultInstructions(existingTemplate.default_instructions ?? '');
    setIsActive(existingTemplate.is_active === 1);
    setItems(existingItems.map(rowToDraft));
    setHydrated(true);
  }, [existingTemplate, existingItems, isNew, hydrated, items.length]);

  // ─── Validation ───

  const validate = useCallback((): boolean => {
    const next: typeof errors = { rows: {} };
    if (!name.trim()) next.name = 'Template name is required.';
    if (items.length === 0) {
      next.items = 'Add at least one medicine row.';
    } else {
      for (const row of items) {
        if (!row.inventoryItemId && !row.itemName.trim()) {
          (next.rows ??= {})[row.id] = 'Pick an inventory item or enter a medicine name.';
        } else if (row.durationValue.trim() && Number.isNaN(parseInt(row.durationValue, 10))) {
          (next.rows ??= {})[row.id] = 'Duration must be a number.';
        }
      }
    }
    setErrors(next);
    return !next.name && !next.items && Object.keys(next.rows ?? {}).length === 0;
  }, [name, items]);

  // ─── Item helpers ───

  const updateItem = useCallback((id: string, changes: Partial<ItemDraft>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const addItem = useCallback(() => {
    setItems((prev) => [...prev, emptyItemDraft(prev.length)]);
  }, []);

  const moveItem = useCallback((id: string, direction: -1 | 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      next.splice(target, 0, removed);
      return next.map((r, i) => ({ ...r, sortOrder: i }));
    });
  }, []);

  const applyInventorySelection = useCallback(
    (rowId: string, sel: InventoryPickerSelection) => {
      updateItem(rowId, {
        inventoryItemId: sel.inventoryItemId,
        itemName: sel.itemName,
        itemDetails: buildItemDetailsFromSelection(sel),
        // Snapshots — captured at authoring time, frozen on later inventory edits.
        itemNameSnapshot: sel.itemName,
        skuSnapshot: sel.sku,
        genericNameSnapshot: sel.genericName,
        brandNameSnapshot: sel.brandName,
        strengthSnapshot: sel.strength,
        dosageFormSnapshot: sel.dosageForm,
        routeSnapshot: sel.route,
        unitSnapshot: sel.inventoryUnit,
        // Apply inventory defaults to the prescribing fields when they are
        // still blank, so the user starts from sensible defaults.
        doseQuantity: sel.defaultDosage ?? sel.dose ?? '',
      });
    },
    [updateItem],
  );

  const clearInventoryBinding = useCallback(
    (rowId: string) => {
      updateItem(rowId, {
        inventoryItemId: null,
        // Keep itemName so the user can keep editing it as free text.
      });
    },
    [updateItem],
  );

  // ─── Save ───

  const handleSave = useCallback(async () => {
    if (!db) return;
    if (!validate()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const id = isNew ? crypto.randomUUID() : (templateId as string);

      if (isNew) {
        await db.execute(
          `INSERT INTO prescription_templates
            (id, org_id, name, description, category, specialty,
             diagnosis_code, diagnosis_label, default_instructions,
             is_active, created_by, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            name.trim(),
            description.trim() || null,
            category.trim() || null,
            specialty.trim() || null,
            // Diagnosis columns retained in schema but no longer authored in the
            // builder UI — preserved for future re-enable without a migration.
            null,
            null,
            defaultInstructions.trim() || null,
            isActive ? 1 : 0,
            userId,
            now,
            now,
          ],
        );
      } else {
        await db.execute(
          `UPDATE prescription_templates
              SET name = ?, description = ?, category = ?, specialty = ?,
                  default_instructions = ?, is_active = ?, updated_at = ?
            WHERE id = ?`,
          [
            name.trim(),
            description.trim() || null,
            category.trim() || null,
            specialty.trim() || null,
            defaultInstructions.trim() || null,
            isActive ? 1 : 0,
            now,
            id,
          ],
        );

        // Replace all item rows for the template (PRD.218 §11).
        await db.execute(`DELETE FROM prescription_template_items WHERE template_id = ?`, [id]);
      }

      // Insert items in current order.
      for (let i = 0; i < items.length; i++) {
        const row = items[i];
        const durTrimmed = row.durationValue.trim();
        const durNum = durTrimmed === '' ? null : parseInt(durTrimmed, 10);
        await db.execute(
          `INSERT INTO prescription_template_items
            (id, org_id, template_id, inventory_item_id,
             item_name, item_details,
             item_name_snapshot, sku_snapshot, generic_name_snapshot, brand_name_snapshot,
             strength_snapshot, dosage_form_snapshot, route_snapshot, unit_snapshot,
             intake_timing, dose_quantity,
             schedule_morning, schedule_afternoon, schedule_night,
             duration_value, duration_unit, instructions, sort_order,
             created_at, updated_at)
           VALUES (?, ?, ?, ?,
                   ?, ?,
                   ?, ?, ?, ?,
                   ?, ?, ?, ?,
                   ?, ?,
                   ?, ?, ?,
                   ?, ?, ?, ?,
                   ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            id,
            row.inventoryItemId,
            row.itemName.trim(),
            row.itemDetails.trim() || null,
            row.itemNameSnapshot,
            row.skuSnapshot,
            row.genericNameSnapshot,
            row.brandNameSnapshot,
            row.strengthSnapshot,
            row.dosageFormSnapshot,
            row.routeSnapshot,
            row.unitSnapshot,
            row.intakeTiming || null,
            row.doseQuantity.trim() || null,
            row.scheduleMorning ? 1 : 0,
            row.scheduleAfternoon ? 1 : 0,
            row.scheduleNight ? 1 : 0,
            durNum != null && !Number.isNaN(durNum) ? durNum : null,
            row.durationUnit || null,
            row.instructions.trim() || null,
            i,
            now,
            now,
          ],
        );
      }

      onClose();
    } catch (err) {
      console.error('[PrescriptionTemplates] save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [
    db,
    validate,
    isNew,
    templateId,
    orgId,
    name,
    description,
    category,
    specialty,
    defaultInstructions,
    isActive,
    userId,
    items,
    onClose,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-5)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {onToggleSidebar && (
          <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
        )}
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {isNew ? 'New Prescription Template' : 'Edit Prescription Template'}
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Author a reusable preset. Saving will not change prescriptions already on patients.
          </p>
        </div>
        <button onClick={onClose} style={mutedBtnStyle}>
          <NavIcon name="chevron-left" /> Back
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '8px 16px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <NavIcon name="check" /> {saving ? 'Saving…' : 'Save Template'}
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-6)' }}>
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-6)',
          }}
        >
          {/* Template details */}
          <Section title="Template Details">
            <FieldGrid>
              <Field label="Template Name" required error={errors.name}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Upper Respiratory Infection"
                  style={inputStyle}
                />
              </Field>
              <Field label="Status">
                <button
                  type="button"
                  onClick={() => setIsActive((v) => !v)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: isActive
                      ? 'var(--color-success-bg, #dcfce7)'
                      : 'var(--bg-surface-2, #f1f5f9)',
                    color: isActive ? 'var(--color-success-text, #166534)' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'currentColor',
                    }}
                  />
                  {isActive ? 'Active' : 'Inactive'}
                </button>
              </Field>
              <Field label="Description" full>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Short description of when to use this template (optional)."
                  style={textareaStyle}
                />
              </Field>
              <Field label="Category">
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. URI, Dental, Fever"
                  style={inputStyle}
                />
              </Field>
              <Field label="Specialty">
                <input
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="e.g. General, Dermatology"
                  style={inputStyle}
                />
              </Field>
            </FieldGrid>
          </Section>

          {/* Default instructions */}
          <Section title="Default Instructions">
            <textarea
              value={defaultInstructions}
              onChange={(e) => setDefaultInstructions(e.target.value)}
              rows={3}
              placeholder="Patient-facing instructions, e.g. Increase oral fluid intake. Return for consult if symptoms worsen."
              style={textareaStyle}
            />
          </Section>

          {/* Medicines */}
          <Section
            title="Medicines"
            actions={
              <button
                type="button"
                onClick={addItem}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <NavIcon name="plus" /> Add Medicine
              </button>
            }
          >
            {errors.items && <ErrorText>{errors.items}</ErrorText>}
            {items.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                No medicines yet. Add at least one row to save the template.
              </div>
            ) : (
              <MedicineTable
                items={items}
                errors={errors.rows ?? {}}
                onUpdate={updateItem}
                onRemove={removeItem}
                onMoveUp={(id) => moveItem(id, -1)}
                onMoveDown={(id) => moveItem(id, 1)}
                onSelectInventory={applyInventorySelection}
                onClearInventory={clearInventoryBinding}
              />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

// ─── Section / Field building blocks ───

function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            color: 'var(--text)',
          }}
        >
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  error,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        gridColumn: full ? '1 / -1' : undefined,
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--color-danger-text, #e53e3e)' }}> *</span>}
      </span>
      {children}
      {error && <ErrorText>{error}</ErrorText>}
    </label>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-danger-text, #e53e3e)',
      }}
    >
      {children}
    </span>
  );
}

// ─── Medicine table (mirrors encounter Prescription tab columns) ───

function MedicineTable({
  items,
  errors,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onSelectInventory,
  onClearInventory,
}: {
  items: ItemDraft[];
  errors: Record<string, string>;
  onUpdate: (id: string, changes: Partial<ItemDraft>) => void;
  onRemove: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onSelectInventory: (id: string, sel: InventoryPickerSelection) => void;
  onClearInventory: (id: string) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        overflowX: 'auto',
        background: 'var(--bg-surface)',
      }}
    >
      <table
        style={{
          width: '100%',
          minWidth: 1100,
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        <thead>
          <tr style={{ background: 'var(--bg-surface-2, #fafafa)' }}>
            <ColHeader style={{ width: 40, textAlign: 'center' }}>#</ColHeader>
            <ColHeader style={{ minWidth: 220 }}>Item Name</ColHeader>
            <ColHeader style={{ minWidth: 180 }}>Strength / Dose</ColHeader>
            <ColHeader style={{ minWidth: 140 }}>When to Take</ColHeader>
            <ColHeader style={{ minWidth: 90 }}>Qty</ColHeader>
            <ColHeader style={{ minWidth: 140, textAlign: 'center' }}>Schedule</ColHeader>
            <ColHeader style={{ minWidth: 160 }}>Duration</ColHeader>
            <ColHeader style={{ minWidth: 180 }}>Instructions</ColHeader>
            <ColHeader style={{ width: 110, textAlign: 'right' }}>Actions</ColHeader>
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => (
            <MedicineRow
              key={row.id}
              row={row}
              index={idx}
              isFirst={idx === 0}
              isLast={idx === items.length - 1}
              error={errors[row.id]}
              onUpdate={(changes) => onUpdate(row.id, changes)}
              onRemove={() => onRemove(row.id)}
              onMoveUp={() => onMoveUp(row.id)}
              onMoveDown={() => onMoveDown(row.id)}
              onSelectInventory={(sel) => onSelectInventory(row.id, sel)}
              onClearInventory={() => onClearInventory(row.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ColHeader({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function MedicineRow({
  row,
  index,
  isFirst,
  isLast,
  error,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onSelectInventory,
  onClearInventory,
}: {
  row: ItemDraft;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  error?: string;
  onUpdate: (changes: Partial<ItemDraft>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSelectInventory: (sel: InventoryPickerSelection) => void;
  onClearInventory: () => void;
}) {
  return (
    <tr style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
      <td style={cellStyle}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            paddingTop: 4,
          }}
        >
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {index + 1}
          </span>
        </div>
      </td>

      <td style={cellStyle}>
        <MedicinePicker
          value={row.itemName}
          inventoryItemId={row.inventoryItemId}
          onTypeFreeText={(text) => onUpdate({ itemName: text, inventoryItemId: null })}
          onSelectInventory={onSelectInventory}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
          }}
        >
          {row.inventoryItemId ? (
            <SourceBadge kind="inventory">Inventory-linked</SourceBadge>
          ) : (
            <SourceBadge kind="free">Free-text</SourceBadge>
          )}
          {row.inventoryItemId && (
            <button
              type="button"
              onClick={onClearInventory}
              style={linkBtnStyle}
              title="Unlink inventory item — keeps the row as free-text"
            >
              Unlink
            </button>
          )}
        </div>
        {error && (
          <div style={{ marginTop: 4 }}>
            <ErrorText>{error}</ErrorText>
          </div>
        )}
      </td>

      <td style={cellStyle}>
        <input
          value={row.itemDetails}
          onChange={(e) => onUpdate({ itemDetails: e.target.value })}
          placeholder="Brand, strength, formulation"
          style={inputStyle}
        />
      </td>

      <td style={cellStyle}>
        <select
          value={row.intakeTiming}
          onChange={(e) => onUpdate({ intakeTiming: e.target.value })}
          style={inputStyle}
        >
          {INTAKE_TIMING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>

      <td style={cellStyle}>
        <input
          value={row.doseQuantity}
          onChange={(e) => onUpdate({ doseQuantity: e.target.value })}
          placeholder="1 tab"
          style={inputStyle}
        />
      </td>

      <td style={cellStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          <SchedulePill
            label="M"
            active={row.scheduleMorning}
            onClick={() => onUpdate({ scheduleMorning: !row.scheduleMorning })}
          />
          <SchedulePill
            label="A"
            active={row.scheduleAfternoon}
            onClick={() => onUpdate({ scheduleAfternoon: !row.scheduleAfternoon })}
          />
          <SchedulePill
            label="N"
            active={row.scheduleNight}
            onClick={() => onUpdate({ scheduleNight: !row.scheduleNight })}
          />
        </div>
      </td>

      <td style={cellStyle}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            value={row.durationValue}
            onChange={(e) => onUpdate({ durationValue: e.target.value })}
            inputMode="numeric"
            placeholder="5"
            style={{ ...inputStyle, width: 64 }}
          />
          <select
            value={row.durationUnit}
            onChange={(e) => onUpdate({ durationUnit: e.target.value })}
            style={{ ...inputStyle, flex: 1 }}
          >
            {DURATION_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </td>

      <td style={cellStyle}>
        <input
          value={row.instructions}
          onChange={(e) => onUpdate({ instructions: e.target.value })}
          placeholder="e.g. Take in lukewarm water"
          style={inputStyle}
        />
      </td>

      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            style={{ ...iconBtnStyle, opacity: isFirst ? 0.4 : 1 }}
            title="Move up"
            aria-label="Move up"
          >
            ▲
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            style={{ ...iconBtnStyle, opacity: isLast ? 0.4 : 1 }}
            title="Move down"
            aria-label="Move down"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onRemove}
            style={{
              ...iconBtnStyle,
              color: 'var(--color-danger-text, #e53e3e)',
            }}
            title="Remove row"
            aria-label="Remove row"
          >
            <NavIcon name="trash" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function SchedulePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        borderRadius: 'var(--radius-sm)',
        border: active ? '1.5px solid var(--color-primary)' : '1px solid var(--border)',
        background: active ? 'rgba(59,130,246,0.10)' : 'var(--bg-surface)',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {label}
    </button>
  );
}

// ─── Inline medicine picker ───

function MedicinePicker({
  value,
  inventoryItemId,
  onTypeFreeText,
  onSelectInventory,
}: {
  value: string;
  inventoryItemId: string | null;
  onTypeFreeText: (text: string) => void;
  onSelectInventory: (sel: InventoryPickerSelection) => void;
}) {
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const anchorRef = useRef<HTMLDivElement | null>(null);
  // PRD.198 fix — the table cell uses `overflow-x: auto`, which clips any
  // absolutely-positioned dropdown. Portal the menu to document.body and
  // position it from the anchor's bounding rect so it escapes the clip.
  // `placement` flips the menu above the input when the row is near the
  // bottom of the viewport.
  const [anchorRect, setAnchorRect] = useState<{
    top: number; // y to use as `top` (below) or `bottom` (above) — see placement
    left: number;
    width: number;
    placement: 'below' | 'above';
    maxHeight: number;
  } | null>(null);
  const DROPDOWN_MAX_HEIGHT = 280;
  const DROPDOWN_MARGIN = 8;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  const { data: rows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    qInventoryPickerCandidates(debounced, ['medicine']),
  );

  const trimmed = value.trim();
  const showDropdown = open && (rows.length > 0 || trimmed.length > 0);
  const exactMatch = useMemo(
    () => rows.some((r) => (r.item_name ?? '').toLowerCase() === trimmed.toLowerCase()),
    [rows, trimmed],
  );

  // Track the anchor's screen position while the dropdown is open. Choose
  // placement based on which side has more room and clamp the menu's
  // max-height so it never spills off-screen.
  useLayoutEffect(() => {
    if (!showDropdown) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const spaceBelow = viewportH - r.bottom - DROPDOWN_MARGIN;
      const spaceAbove = r.top - DROPDOWN_MARGIN;
      const wantsBelow = spaceBelow >= DROPDOWN_MAX_HEIGHT || spaceBelow >= spaceAbove;
      if (wantsBelow) {
        setAnchorRect({
          top: r.bottom + 4,
          left: r.left,
          width: r.width,
          placement: 'below',
          maxHeight: Math.max(160, Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow)),
        });
      } else {
        setAnchorRect({
          // For `above` we store the distance from the viewport bottom so
          // the dropdown can be positioned via `bottom` instead of `top`.
          top: viewportH - r.top + 4,
          left: r.left,
          width: r.width,
          placement: 'above',
          maxHeight: Math.max(160, Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove)),
        });
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showDropdown]);

  // Dismiss on outside click (the portaled dropdown lives outside anchorRef
  // so we check it by data attribute).
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      let el: Node | null = target;
      while (el && el !== document.body) {
        if (el instanceof HTMLElement && el.dataset?.pickerDropdown === 'template-medicine') {
          return;
        }
        el = el.parentNode;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={(e) => {
          onTypeFreeText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          inventoryItemId ? 'Inventory-linked medicine' : 'Search medicine, product, or type…'
        }
        style={inputStyle}
      />
      {showDropdown &&
        anchorRect &&
        createPortal(
          <div
            role="listbox"
            data-picker-dropdown="template-medicine"
            style={{
              position: 'fixed',
              ...(anchorRect.placement === 'below'
                ? { top: anchorRect.top }
                : { bottom: anchorRect.top }),
              left: anchorRect.left,
              width: Math.max(anchorRect.width, 320),
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              maxHeight: anchorRect.maxHeight,
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            {rows.length > 0 && <div style={dropdownLabelStyle}>Inventory Medicine</div>}
            {rows.slice(0, 12).map((r) => {
              const sel = pickerCandidateRowToSelection(r);
              return (
                <button
                  key={r.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelectInventory(sel);
                    setOpen(false);
                  }}
                  style={dropdownOptionStyle}
                >
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                    {sel.itemName}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {[
                      sel.genericName && `Generic: ${sel.genericName}`,
                      sel.brandName && `Brand: ${sel.brandName}`,
                      sel.strength,
                      sel.dosageForm,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </div>
                </button>
              );
            })}
            {trimmed.length > 0 && !exactMatch && (
              <>
                <div style={dropdownLabelStyle}>Manual</div>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen(false);
                  }}
                  style={dropdownOptionStyle}
                >
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                    Use “{trimmed}” as free-text medicine
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    Saved without inventory linkage.
                  </div>
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── Source badge ───

function SourceBadge({
  kind,
  children,
}: {
  kind: 'inventory' | 'free';
  children: React.ReactNode;
}) {
  const styles: React.CSSProperties =
    kind === 'inventory'
      ? {
          background: 'var(--color-success-bg, #dcfce7)',
          color: 'var(--color-success-text, #166534)',
        }
      : {
          background: 'var(--bg-surface-2, #f1f5f9)',
          color: 'var(--text-muted)',
        };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...styles,
      }}
    >
      {children}
    </span>
  );
}

// ─── Styles ───

const cellStyle: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'top',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  outline: 'none',
  fontFamily: 'inherit',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 60,
};

const mutedBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
};

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
};

const linkBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
};

const dropdownLabelStyle: React.CSSProperties = {
  padding: '6px 10px 4px',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  background: 'var(--bg-surface-2, #fafafa)',
  borderBottom: '1px solid var(--border)',
};

const dropdownOptionStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  background: 'transparent',
  cursor: 'pointer',
};
